import { round } from "lodash";

import { SaturationCurve } from "../types/saturationCurve";

export type WeeklyMetricAverages = {
  totalSpend: number;
  totalRevenue: number;
  meanSpendPerWeek: number;
  meanRevenuePerWeek: number;
  meanYieldPerWeek: number;
  meanRoiPerWeek: number;
};

export const WeeklyMetricAveragesMapping = {
  TOTAL_SPEND: "Total spend for period",
  TOTAL_REVENUE: "Total revenue for period",
  TOTAL_RETURN: "Total return for period",
  MEAN_SPEND_PER_WEEK: "Mean marketing spend",
  MEAN_REVENUE_PER_WEEK: "Mean marketing revenue",
  MEAN_RETURN_PER_WEEK: "Mean marketing return",
  MEAN_YIELD_PER_WEEK: "Mean marketing yield for period",
  MEAN_ROI_PER_WEEK: "Mean marketing ROI for period",
};

const calculateTotals = (saturationCurves: Partial<SaturationCurve>[]) =>
  saturationCurves.reduce(
    (acc, curve) => ({
      totalSpend: acc.totalSpend + (curve.spend ?? 0),
      totalRevenue: acc.totalRevenue + (curve.revenue ?? 0),
      totalActiveWeeks: acc.totalActiveWeeks + (curve.activeWeeks ?? 0),
    }),
    { totalSpend: 0, totalRevenue: 0, totalActiveWeeks: 0 }
  );

const calculateMean = (total: number, weeks: number) => (weeks ? round(total / weeks, 2) : null);

const calculateMeanYield = (meanRevenue: number, meanSpend: number) =>
  round(meanRevenue / meanSpend, 2);

const calculateMeanRoi = (meanRevenue: number, meanSpend: number) =>
  round(((meanRevenue - meanSpend) / meanSpend) * 100, 2);

export const calculateWeeklyMetricAverages = (
  saturationCurves: Partial<SaturationCurve>[] = []
): WeeklyMetricAverages | null => {
  const { totalSpend, totalRevenue, totalActiveWeeks } = calculateTotals(saturationCurves);

  const meanSpendPerWeek = calculateMean(totalSpend, totalActiveWeeks);
  const meanRevenuePerWeek = calculateMean(totalRevenue, totalActiveWeeks);

  if (meanSpendPerWeek === null || meanRevenuePerWeek === null || meanSpendPerWeek === 0) {
    return null;
  }

  const meanYieldPerWeek = calculateMeanYield(meanRevenuePerWeek, meanSpendPerWeek);
  const meanRoiPerWeek = calculateMeanRoi(meanRevenuePerWeek, meanSpendPerWeek);

  return {
    totalSpend,
    totalRevenue,
    meanSpendPerWeek,
    meanRevenuePerWeek,
    meanYieldPerWeek,
    meanRoiPerWeek,
  };
};
